'use client';
import { useRef, useEffect, useState } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

interface MarqueeBannerProps extends BoxProps {
  imgSrc: string;
  imgAlt?: string;
  imgSize?: number;
  duration?: number; 
  gap?: number;
  bg?: string;
}

const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(calc(-1 * var(--shift, 0px))); }
`;

export function MarqueeBanner({
  imgSrc,
  imgAlt = '',
  imgSize = 40,
  duration = 20,
  gap = 32,
  bg = 'gray.50',
  ...rest
}: MarqueeBannerProps) {
  const firstImgRef = useRef<HTMLImageElement | null>(null);
  const laneRef = useRef<HTMLDivElement | null>(null);

  const [count, setCount] = useState(10);
  const [shift, setShift] = useState(0);

  const recalculate = () => {
    const imgWidth = firstImgRef.current?.getBoundingClientRect().width;

    if (!imgWidth) return;

    const viewportWidth = window.innerWidth;

    const imagesNeeded =
      Math.ceil(viewportWidth / (imgWidth + gap)) + 2;

    setCount(imagesNeeded);

    requestAnimationFrame(() => {
      if (laneRef.current) {
        setShift(laneRef.current.scrollWidth);
      }
    });
  };

  useEffect(() => {
    recalculate();

    window.addEventListener('resize', recalculate);
    return () => {
      window.removeEventListener('resize', recalculate);
    };
  }, [gap]);

  const images = Array.from({ length: count }, (_, i) => (
    <img
      key={i}
      ref={i === 0 ? firstImgRef : undefined}
      src={imgSrc}
      alt={imgAlt}
      onLoad={i === 0 ? recalculate : undefined}
      style={{
        height: `${imgSize}px`,
        width: 'auto',
        display: 'block',
        flexShrink: 0,
      }}
    />
  ));

  return (
    <Box overflow="hidden" width="100%" height={`${imgSize}px`} bg={bg} {...rest}>
      <Box
        display="flex"
        width="max-content"
        css={{
          '--shift': `${shift}px`,
          animation: `${marquee} ${duration}s linear infinite`,
          willChange: 'transform',
        }}
      >
        <Box ref={laneRef} display="flex" gap={`${gap}px`} flexShrink={0}>
          {images}
        </Box>

        <Box display="flex" gap={`${gap}px`} flexShrink={0} aria-hidden>
          {images}
        </Box>
      </Box>
    </Box>
  );
}